/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useState } from "react";
import { FieldCode, FieldGroup, IFieldCodeProps } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { useFloatingPanel } from "../../../ui/hooks/useFloatingPanel";
import { FloatingPanelExpressionEditorModel } from "../../../ui/components/FloatingPanelExpressionEditor/FloatingPanelExpressionEditorModel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { IInspectorControl_Expression, TInspectorControlProps } from "../InspectorControlTypes";
import { isModelValueRequired } from "@adapptio/lib-blueprint";
import { useReferenceSelectorContext } from "../../../ui/contexts/ReferenceSelectorContext";

export interface IInspectorControlExpressionProps
	extends TInspectorControlProps<IInspectorControl_Expression> {
	label?: string;
}

export const InspectorControlExpression: React.FC<IInspectorControlExpressionProps> = ({
	model: _model,
	label
}) => {
	const inspectorContext = useEditorInspectorContext();
	const referenceSelectorCtx = useReferenceSelectorContext();

	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "expression");
	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);

	const required = isModelValueRequired(model);

	const language: IFieldCodeProps["language"] = "adapptioExpression";

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleOuterReferenceClick = useCallback<IFieldCodeProps["onOuterReferenceClick"]>(
		async (editorInstance) => {
			try {
				const selectedRef = await referenceSelectorCtx.getSelectedReference();

				if (selectedRef?.path) {
					handleChange(selectedRef.path);
					editorInstance.focus();
				}
			} catch (e) {
				// nvm
			}
		},
		[]
	);

	const { togglePanel, opened } = useFloatingPanel(
		FloatingPanelExpressionEditorModel,
		`panel-${model?.nodeId}`,
		{
			model: _model,
			path: [],
			readOnly: inspectorContext?.readOnly,
			debounceTimeout: inspectorContext?.inputDebounceTimeout,
			title: label,
			language,
			defaultRect: { width: 500, height: 400 },
			onOuterReferenceClick: handleOuterReferenceClick
		}
	);

	useEffect(() => {
		if (model) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldGroup grow>
			<FieldCode
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors.concat(runtimeErrors)}
				parseErrors={model.expParseErrors}
				resultData={expLastValue?.result}
				grow
				language={language}
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				canExpand
				onExpand={togglePanel}
				isExpanded={opened}
				clearable={!required}
				onOuterReferenceClick={handleOuterReferenceClick}
			/>
		</FieldGroup>
	);
};

InspectorControlExpression.defaultProps = {
	label: ""
};
